import type { TagDTO, WorkspaceBySlug } from '@trevize/api/types'

import type { ZeroQueryContext } from '#zero/zero-default-types.ts'

type ZeroMemberRow = {
  userId: string
  role: string
  user?: {
    id: string
    email?: string | null
    image?: string | null
    name?: string | null
  } | null
}

type ZeroSubscriptionRow = {
  id: string
  planId: string
  status:
    | 'incomplete'
    | 'incomplete_expired'
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'unpaid'
    | 'paused'
  accountId: string
  startedAt: number
  cancelAt?: number | null
  canceledAt?: number | null
  trialEndsAt?: number | null
  cancelAtPeriodEnd?: boolean | null
  currentPeriodStart: number
  currentPeriodEnd: number
  createdAt?: number | null
  updatedAt?: number | null
}

/** Raw row from {@linkcode queries.workspaces.bySlug} (related graph). */
export type ZeroWorkspaceBySlugRow = {
  id: string
  ownerId?: string | null
  slug: string
  name: string
  logo?: string | null
  maxConcurrentImplementations?: number | null
  gitCommitAttributionMode?: 'tervezo' | 'user' | null
  knowledgeEnabled?: boolean | null
  autoFixCiEnabled?: boolean | null
  githubScreenshotSharingEnabled?: boolean | null
  linearTaskRuntime?: 'implementation' | 'environment' | null
  featureFlags?: ReadonlyArray<string> | null
  defaultSandboxImageId?: string | null
  createdAt?: number | null
  updatedAt?: number | null
  members?: ReadonlyArray<ZeroMemberRow | null> | null
  subscription?: ZeroSubscriptionRow | null
  tags?: ReadonlyArray<{
    id: string
    name?: string | null
    color?: string | null
  } | null> | null
  workspaceIntegrations?: ReadonlyArray<{
    integrationId: string
    status: string
  } | null> | null
  githubInstallations?: ReadonlyArray<{
    status?: 'active' | 'inactive' | 'suspended' | null
  } | null> | null
}

function numToDate(n: number | null | undefined): Date | null {
  if (n === null || n === undefined) {
    return null
  }
  return new Date(n)
}

function optionalDate(n: number | null | undefined): Date | undefined {
  if (n === null || n === undefined) {
    return undefined
  }
  return new Date(n)
}

function subscriptionFromZero(
  s: ZeroSubscriptionRow | null | undefined,
): WorkspaceBySlug['subscription'] {
  if (s === null || s === undefined) {
    return null
  }
  return {
    id: s.id,
    planId: s.planId,
    status: s.status,
    accountId: s.accountId,
    startedAt: new Date(s.startedAt),
    cancelAt: optionalDate(s.cancelAt ?? undefined),
    canceledAt: optionalDate(s.canceledAt ?? undefined),
    trialEndsAt: optionalDate(s.trialEndsAt ?? undefined),
    cancelAtPeriodEnd:
      s.cancelAtPeriodEnd === null ? undefined : s.cancelAtPeriodEnd,
    currentPeriodStart: new Date(s.currentPeriodStart),
    currentPeriodEnd: new Date(s.currentPeriodEnd),
    createdAt: optionalDate(s.createdAt ?? undefined),
    updatedAt: optionalDate(s.updatedAt ?? undefined),
  }
}

/**
 * Maps a Zero workspace row into the former tRPC `workspaces.bySlug` shape.
 * Returns null when the row is missing or the user is not a workspace member.
 */
export function workspaceFromZero(
  row: ZeroWorkspaceBySlugRow | undefined,
  ctx: ZeroQueryContext | undefined,
): WorkspaceBySlug | null {
  if (row === undefined || ctx === undefined) {
    return null
  }

  const userId = ctx.user.id
  const membersList =
    row.members?.filter((m): m is ZeroMemberRow => m != null) ?? []
  const isMember = membersList.some((m) => m.userId === userId)
  if (!isMember) {
    return null
  }

  const ownerId = row.ownerId ?? null

  const activeGithub = (row.githubInstallations ?? []).some(
    (g) => g != null && g.status === 'active',
  )

  return {
    id: row.id,
    ownerId,
    slug: row.slug,
    name: row.name,
    logo: row.logo ?? null,
    gitCommitAttributionMode: row.gitCommitAttributionMode ?? 'tervezo',
    knowledgeEnabled: row.knowledgeEnabled ?? false,
    autoFixCiEnabled: row.autoFixCiEnabled ?? false,
    githubScreenshotSharingEnabled: row.githubScreenshotSharingEnabled ?? true,
    linearTaskRuntime: row.linearTaskRuntime ?? 'implementation',
    featureFlags: [...(row.featureFlags ?? [])],
    defaultSandboxImageId: row.defaultSandboxImageId ?? null,
    maxConcurrentImplementations: row.maxConcurrentImplementations ?? null,
    createdAt: numToDate(row.createdAt ?? undefined) ?? new Date(0),
    updatedAt: numToDate(row.updatedAt ?? undefined) ?? new Date(0),
    subscription: subscriptionFromZero(row.subscription ?? null),
    members: membersList.map((member) => {
      const u = member.user
      const id = u?.id ?? member.userId
      return {
        id,
        email: u?.email ?? null,
        image: u?.image ?? null,
        name: u?.name ?? null,
        roles: id === ownerId ? ['owner'].concat(member.role) : [member.role],
      }
    }),
    enabledIntegrations: [
      ...(row.workspaceIntegrations ?? [])
        .filter((wi): wi is NonNullable<typeof wi> => wi != null)
        .map((wi) => ({
          integrationId: wi.integrationId,
          status: wi.status,
        })),
      ...(activeGithub
        ? [{ integrationId: 'github' as const, status: 'active' as const }]
        : []),
    ],
    tags: (row.tags ?? [])
      .filter((t): t is NonNullable<typeof t> => t != null)
      .map(
        (t): TagDTO => ({
          id: t.id,
          name: t.name ?? '',
          color: t.color ?? null,
        }),
      ),
  }
}
